'use client'

import { useEffect } from 'react'

import { ComponentStackReportedError } from '@/components/client/shared/component-stack-boundary'
import { PublicStyles } from '@/components/server/styles/public-styles'
import { useTranslation } from '@/hooks/client/use-translation'
import { sentryCall } from '@/lib/log/sentry-client'

type Props = {
  error: ComponentStackReportedError & { digest?: string }
  reset: () => void
}

// Scoped error boundary for story + compare pages. The story render gate
// (readValidatedStoryForRender) throws when a story's embedded_data is still
// incomplete after a rebake; that surfaces here as an HTTP 500 instead of a
// blank page, and is reported to Sentry. Scoped to (story-pages) so a single
// broken story doesn't take down sibling routes.
export default function StoryPagesError({ error, reset }: Props) {
  const { t } = useTranslation()

  useEffect(() => {
    // Skip if ComponentStackBoundary already reported this (with the React
    // componentStack) and re-threw it to us — avoids a duplicate Sentry event.
    if (error.__componentStackReported) {
      return
    }
    sentryCall((Sentry) => Sentry.captureException(error))
  }, [error])

  return (
    <main className="flex min-h-[60vh] flex-col items-center justify-center gap-6 px-4 py-16">
      <PublicStyles />
      <h1 className="text-navy text-4xl font-bold">{t('error.title', 'Something went wrong')}</h1>
      <p className="text-navy-60 text-center text-lg">
        {t('error.message', 'An unexpected error occurred. Our team has been notified.')}
      </p>
      <button
        type="button"
        onClick={reset}
        data-tracking="ignore"
        className="bg-lapis hover:bg-navy cursor-pointer rounded-full px-6 py-3 text-white transition-colors"
      >
        {t('error.retry', 'Try again')}
      </button>
    </main>
  )
}
